<template>
  <div
    :class="{'has_error': has_error}">
    <sForm
      ref="criterionTypeConditionsForm"
      label-position="top">
      <div
        class="flex pb-4">
        <sFormItem
          :class="[{'width-100' :type != 'NUMERIC_INPUT'} , 'ma-0']"
          :label="$t('Tenders.TypeOfData')"
          prop="criterionControlType">
          <sSelect
            :class="[{'width-100' :type != 'NUMERIC_INPUT'} , 'criteria-type__type-select input']"
            data-qa="criterion-type"
            :placeholder="$t('Tenders.TypeOfData')"
            :value.sync="type"
            @update:value="changeType">
            <sOption
              v-for="typeItem in types"
              :key="`criterion-type-${typeItem.value}`"
              :data-qa="`criterion-type-${typeItem.value}`"
              :label="typeItem.text"
              :value="typeItem.value"/>
          </sSelect>
        </sFormItem>
        <sFormItem
          v-if="type == 'NUMERIC_INPUT'"
          class="my-0 ml-5"
          :label="$t('Tenders.UnitOfMeasurement')"
          prop="measuringUnit">
          <sSelect
            clearable
            data-qa="criterion-measurement"
            filterable
            :placeholder="$t('Tenders.UnitOfMeasurement')"
            style="width: 100%"
            :value.sync="element.measuringUnit"
            value-key="unitCode">
            <sOption
              v-for="(measurementItem, measurementItemIndex) in measurements"
              :key="`criterion-unit-${measurementItem.unitCode}-${measurementItemIndex}`"
              :data-qa="`criterion-unit-item-${measurementItem.unitCode}`"
              :label="measurementItem.unitTitle"
              :value="measurementItem"/>
          </sSelect>
        </sFormItem>
      </div>
      <div
        v-if="type === 'NUMERIC_INPUT' || type === 'DICTIONARY'"
        class="h5-headline">
        {{ $t('Tenders.OptionalOptions') }}
        <sDivider class="my-3"/>
      </div>
      <transition-group
        v-if="type"
        appear
        class="dop-fileds_rows"
        name="s-fade-in"
        tag="div">
        <template v-if="type === 'NUMERIC_INPUT' || type === 'BIG_TEXT'">
          <sFormItem
            key="choose-fixed-value"
            data-qa="choose-fixed-value"
            :show-message="false">
            <template #label>
              <div class="h7-text">
                {{ $t('Tenders.CaptureValue') }}
              </div>
              <div class="h8-text mt-2">
                {{ $t('Tenders.ValueWillbeOfferedToSuppliersLine1') }}
              </div>
              <div class="h8-text mt-2">
                {{ $t('Tenders.ValueWillbeOfferedToSuppliersLine2') }}
              </div>
            </template>
            <RadioGroup
              class="mt-2 ml-2"
              data-qa="choose-fixed-value-radio"
              label_class="h7-text"
              :model.sync="is_fixed"
              row
              @change="changeFixed"/>
          </sFormItem>
          <sFormItem
            v-if="is_fixed"
            key="is_fixed"
            :label="$t('Tenders.YourValue')"
            prop="fixedValue"
            :show-message="false"
            style="max-width: 350px;">
            <sInputNumber
              v-if="type == 'NUMERIC_INPUT'"
              ref="fixedValueInput-number"
              allow-zero
              class="mb-1"
              clearable
              data-qa="fixedValueInput"
              :empty-value="null"
              :minus="true"
              placeholder="0.00000"
              :precision="5"
              :use-thousand-separator="true"
              :value.sync="element.fixedValue"/>
            <sInput
              v-else
              class="mb-1"
              data-qa="fixedValueInput-text"
              max-length="254"
              max-rows="6"
              rows="3"
              show-word-limit
              type="textarea"
              :value.sync="element.fixedValue"/>
          </sFormItem>
          <template v-else-if="type == 'NUMERIC_INPUT'">
            <sFormItem
              key="allow-decimals"
              data-qa="allow-decimals"
              :label="$t('Tenders.AllowNon-integerValues')"
              prop="allowDecimals"
              :show-message="false">
              <RadioGroup
                data-qa="allow-decimals-radio"
                label_class="h7-text"
                :model.sync="element.allowDecimals"
                row
                @change="changeDecimals"/>
            </sFormItem>
            <sFormItem
              key="criterion-range"
              data-qa="criterion-range"
              :error="checkCorrectMaxMin"
              :label="$t('Tenders.RangeOfValues')"
              :show-message="!!checkCorrectMaxMin">
              <div class="flex">
                <sInputNumber
                  ref="minValue"
                  allow-zero
                  class="range__min input"
                  clearable
                  data-qa="criterion-range-minValue"
                  :dop-style="{prepend: 'background-color: var(--white-color); padding: 0 0 0 14px;'}"
                  :empty-value="null"
                  :minus="true"
                  :precision="5"
                  :use-thousand-separator="true"
                  :value.sync="element.minValue">
                  <template #prepend>
                    {{ $t('Purchasing.From') }}
                  </template>
                </sInputNumber>
                <sInputNumber
                  ref="maxValue"
                  allow-zero
                  class="range__max input ml-5"
                  clearable
                  data-qa="criterion-range-maxValue"
                  :dop-style="{prepend: 'background-color: var(--white-color); padding: 0 0 0 14px;'}"
                  :empty-value="null"
                  :minus="true"
                  :precision="5"
                  :use-thousand-separator="true"
                  :value.sync="element.maxValue">
                  <template #prepend>
                    {{ $t('Common.till') }}
                  </template>
                </sInputNumber>
              </div>
            </sFormItem>
            <sFormItem
              key="show-best-val"
              data-qa="show-best-val"
              :label="`${$t('Tenders.ShowBestValueToOtherMembers')}?`"
              prop="isBestValueDisplayedWeb"
              :show-message="false">
              <RadioGroup
                data-qa="show-best-val-radio"
                label_class="h7-text"
                :model.sync="element.isBestValueDisplayedWeb"
                row/>
            </sFormItem>
            <sFormItem
              v-if="element.isBestValueDisplayedWeb"
              key="score-direction"
              data-qa="score-direction"
              :label="$t('Tenders.BestValueOnSite')"
              prop="scoreDirection"
              :show-message="false"
              style="max-width: 175px;">
              <RadioGroup
                class="mt-2 ml-2"
                data-qa="score-direction-radio"
                :items="scoreDirectionItems"
                label_class="h7-text"
                :model.sync="element.scoreDirection"/>
            </sFormItem>
            <div
              key="standart-value"
              data-qa="standart-value">
              <sFormItem
                :error="standartValueError"
                :label="$t('Tenders.ReferenceValue')"
                prop="standartValue"
                :show-message="!!standartValueError"
                style="max-width: 175px;">
                <sInputNumber
                  ref="standartValue"
                  allow-zero
                  clearable
                  data-qa="standart-value-input"
                  :empty-value="null"
                  :minus="true"
                  :precision="5"
                  :use-thousand-separator="true"
                  :value.sync="element.standartValue"/>
              </sFormItem>
            </div>
          </template>
        </template>
        <template v-if="type == 'DICTIONARY'">
          <TypeVariants
            key="criteria-type__dictionary"
            :card="card"
            :item="item"
            :items="values"
            @addListItem="addListItem"
            @deleteListItem="deleteListItem"/>
        </template>
        <template v-if="type == 'CALCULATED'">
          <ChangeFormula
            key="change-criterion-formula"
            ref="formula_editor"
            :card.sync="card"
            class="criteria-type__formula"
            :criteria="criteria"
            data-qa="change-criterion-formula"
            :error.sync="formula_error"
            :formula.sync="element.calculationFormula"
            header
            :item="item"
            :reduced-price-available="reducedPriceAvailable"
            @update:error_math="setFormulaError"/>
        </template>
      </transition-group>
    </sForm>
  </div>
</template>
<script>
import RadioGroup from 'components/common/inputs/RadioGroup.vue'
import TypeVariants from 'components/tenders/critetia/TypeVariants.vue'
import ChangeFormula from 'components/tenders/critetia/ChangeFormula.vue'

import { modalMixin } from 'mixins'

/**
 * Редактирование/ввод неценового критерия
 */
export default {
  name: 'criterionTypeConditions',
  mixins: [modalMixin],
  data() {
    return {
      key: 10,
      measurements: [],
      is_fixed: false,
      formula_error: false,
      types: [
        { text: this.$t('Tenders.NUMERIC_INPUT'), value: 'NUMERIC_INPUT'},
        { text: this.$t('Tenders.DICTIONARY'), value: 'DICTIONARY'},
        { text: this.$t('Tenders.BIG_TEXT'), value: 'BIG_TEXT'},
        { text: this.$t('Tenders.CALCULATED'), value: 'CALCULATED'},
      ]
    }
  },
  props: {
    item: Object,
    criteria: Array,
    card: Object,
    error: Boolean,
    reducedPriceAvailable: Boolean
  },
  created() {
    this.is_fixed = !!this.element.fixedValue
    this.$store.dispatch('setTenderMeasurements')
      .then((res) => this.measurements = res
        .map(i => ({
          ...i,
          unitTitle: i.unitTitle.toLowerCase()
        }))
        .sort((a,b) => a.unitTitle?.localeCompare(b.unitTitle))
      )
  },
  computed: {
    type: {
      get() {
        return this.element.criterionControlType
      },
      set(val) {
        this.element.criterionControlType = val
      }
    },
    values: {
      get() {
        return this.element.dictionaryValues
      },
      set(val) {
        this.element.dictionaryValues = val
      }
    },
    element: {
      get() {
        return this.item
      },
      set(val) {
        this.$emit('update:item', val)
      }
    },
    scoreDirectionItems() {
      return [
        { label: this.$t('Tenders.MIN') + ' ' + this.$t('Tenders.ofAllOffered'), value: 'MIN'},
        { label: this.$t('Tenders.MAX') + ' ' + this.$t('Tenders.ofAllOffered'), value: 'MAX'},
      ]
    },
    has_error() {
      let result
      switch (this.element.criterionControlType) {
      case 'DICTIONARY' : {

        result = !this.element.dictionaryValues.length
     || this.element.dictionaryValues.some(el => !el.title)
     || Object.values(this.$_.countBy(this.element.dictionaryValues, 'title')).some(el => el > 1)
     || (!!this.element.formulaCode && !!this.element.dictionaryValues.every(el => !el.score))

        break
      }
      case 'NUMERIC_INPUT' : {
        result = this.is_fixed ? !this.element.fixedValue :
          (!!this.standartValueError || !!this.checkCorrectMaxMin)
        break
      }
      case 'CALCULATED' : {
        result = !!this.formula_error
        break
      }
      case 'BIG_TEXT' : {
        result = this.is_fixed && !this.element.fixedValue
        break
      }
      default: {
        result = false
      }
      }

      this.$emit('update:error', result)
      return result
    },
    standartValueError() {
      if ((this.element.standartValue === null || this.element.standartValue === undefined)
    || ((this.element.minValue === null || this.element.minValue === undefined)
        && (this.element.maxValue === null || this.element.maxValue === undefined))) {
        return ''
      }

      const less = (this.element.minValue !== null && this.element.minValue !== undefined) && +this.element.standartValue < +this.element.minValue,
        more = (this.element.maxValue !== null && this.element.maxValue !== undefined) && +this.element.standartValue > +this.element.maxValue,
        is_wrong = less || more

      return is_wrong
        ? `${this.$t('Tenders.EnterValue')}
       ${(less ? `${this.$t('Purchasing.From')} ${this.$n(this.element.minValue, 'decimal', { minimumFractionDigits: this.element.allowDecimals ? 5 : 0 })}` : '')}
       ${(more ? `${this.$t('Common.till')} ${this.$n(this.element.maxValue, 'decimal', { minimumFractionDigits: this.element.allowDecimals ? 5 : 0 })}` : '')}`
        : ''
    },
    checkCorrectMaxMin() {
      if (this.element.minValue != null && this.element.maxValue != null && this.element.minValue > this.element.maxValue) {
        return this.$t('Validate.endValueNeedMoreStartValue')
      }

      return ''
    }
  },
  methods: {
    setFormulaError(val) {
      this.formula_error = !!val.key
      if (val.highlightedFormula !== this.element.calculationFormulaError || this.element.calculationFormula !== this.element.calculationFormula) {
        const result = {
          ...this.element,
          calculationFormulaError: val.highlightedFormula,
          calculationFormula: this.element.calculationFormula
        }
        this.element = result
      }
    },
    check(text, type, ref, cut) {
      this.sliceText(
        text,
        this.$refs[ref]?.$refs?.input,
        type === 'fixedValue' ? 5 : (this.element.allowDecimals ? 5 : 0),
        (val) => this.$set(this.item, type, +this.$_formatValue(val !== null && val !== undefined ? val : 0)),
        cut || 9
      )
    },
    changeDecimals(ev) {
      if (!ev) {
        this.check(this.element.standartValue, 'standartValue', 'standartValue', 10)
        this.element.maxValue = this.element.maxValue ? Math.round(this.element.maxValue) : null
        this.element.minValue = this.element.minValue ? Math.round(this.element.minValue) : null
      }
    },
    deleteListItem(item) {
      this.values = this.values.filter(el => item.id ? el.id != item.id : el.key != item.key)
      this.$forceUpdate()
    },
    addListItem() {
      this.key += 5
      if (!this.values) {
        this.values = []
      }
      this.values.push({ title: '', score: 0, key: this.key})
      this.$forceUpdate()
    },
    changeType(type) {
      this.is_fixed = false
      this.element.fixedValue = ''

      if (type != 'NUMERIC_INPUT') {
        this.element.minValue = null
        this.element.maxValue = null
        this.element.isBestValueDisplayedWeb = false
        this.element.scoreDirection = 'MAX'
        this.element.allowDecimals = false
      }
      if (type != 'CALCULATED') {
        this.element.calculationFormula = ''
        this.element.calculationFormulaError = ''
      }
      if (type != 'DICTIONARY') {
        this.element.dictionaryValues = []
      } else if (!this.element.dictionaryValues?.length) {
        this.addListItem()
        this.addListItem()
      }
      // if (this.element.fixedValue) {
      //  if (type == 'BIG_TEXT') {
      //   this.element.fixedValue = '' + this.element.fixedValue
      //  } else if (type == 'NUMERIC_INPUT') {
      //   this.check(this.element.fixedValue, 'fixedValue', 'fixedValueInput', 16)
      //  }
      // }
    },
    changeFixed(is_fixed) {

      if (is_fixed) {
        this.element.minValue = null
        this.element.maxValue = null
        this.element.isBestValueDisplayedWeb = false
        this.element.scoreDirection = 'MAX'
        this.element.allowDecimals = false
        this.element.standartValue = null
      } else {
        this.element.fixedValue = ''
      }
    }
  },
  components: {
    RadioGroup,
    TypeVariants,
    ChangeFormula,
  }
}
</script>
<style lang="scss" scoped>
.dop-fileds {
 &_rows {
  display: grid;
  gap: 10px;
 }
}
.required::before {
 content: '*';
 margin-right: 5px;
 font-size: 14px;
 color: var(--color-error);
}
.input:not(.width-100) {
 max-width: 175px;
}
</style>
